// src/utils/imageUtils.js
/**
 * Utility functions for image handling
 */

/**
 * Convert file to base64 string
 */
export const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Compress image and convert to base64
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width of the compressed image
 * @param {number} maxHeight - Maximum height of the compressed image
 * @param {number} quality - Image quality (0.1 to 1.0)
 * @returns {Promise<string>} - Base64 encoded compressed image
 */
export const compressImageToBase64 = (file, maxWidth = 800, maxHeight = 600, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress the image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to base64 with compression
      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedBase64);
    };

    img.onerror = reject;

    // Create object URL for the image
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Get file size in MB
 */
export const getFileSizeInMB = (file) => {
  return (file.size / (1024 * 1024)).toFixed(2);
};

/**
 * Validate image file
 */
export const validateImageFile = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSizeInMB = 10;
  
  if (!validTypes.includes(file.type)) {
    return { isValid: false, error: 'Please select a valid image file (JPEG, PNG, GIF, WebP)' };
  }
  
  const fileSizeInMB = getFileSizeInMB(file);
  if (fileSizeInMB > maxSizeInMB) {
    return { isValid: false, error: `File size (${fileSizeInMB}MB) exceeds the maximum limit of ${maxSizeInMB}MB` };
  }
  
  return { isValid: true, error: null };
};